/**
 * Recorta una imagen (data URL) a su contenido visible. Usa edge-scan optimizado:
 * en lugar de iterar todos los píxeles, escanea solo desde los bordes hacia adentro
 * hasta encontrar el primer píxel no-fondo, lo que es órdenes de magnitud más rápido
 * cuando el contenido ocupa la mayor parte del canvas.
 *
 * Auto-detección del modo:
 *   - Si el primer píxel tiene alpha=0 → imagen con fondo transparente → detecta por alpha
 *   - Si tiene alpha=255 → imagen con fondo opaco → detecta por color (no-blanco)
 *
 * @param {string} dataUrl - 'data:image/png;base64,...'
 * @param {Object} opts
 * @param {number}   opts.padding    - píxeles de margen alrededor del contenido (default 15)
 * @param {number[]} opts.background - RGB del fondo en modo color (default [255, 255, 255])
 * @param {number}   opts.tolerance  - tolerancia por componente para considerar "fondo" (default 30)
 * @returns {Promise<string|null>} dataUrl recortado, o **null** si la imagen es todo fondo
 *                                 (para que el caller pueda omitir capturas vacías).
 */
export function cropToContent(dataUrl, opts = {}) {
  const padding    = opts.padding    ?? 15
  const background = opts.background ?? [255, 255, 255]
  const tolerance  = opts.tolerance  ?? 30
  const [br, bg, bb] = background

  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const w = img.width, h = img.height
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      const data = ctx.getImageData(0, 0, w, h).data

      // Si el pixel [0,0] tiene alpha=0, asumimos fondo transparente y detectamos por alpha.
      // Más confiable que detectar por color cuando el fondo es transparente.
      const transparentMode = data[3] === 0

      const isContent = transparentMode
        ? (x, y) => data[(y * w + x) * 4 + 3] > 0
        : (x, y) => {
            const i = (y * w + x) * 4
            return Math.abs(data[i]     - br) > tolerance ||
                   Math.abs(data[i + 1] - bg) > tolerance ||
                   Math.abs(data[i + 2] - bb) > tolerance
          }

      // 1. Top-down: primera fila con contenido
      let minY = -1
      outer1: for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          if (isContent(x, y)) { minY = y; break outer1 }
        }
      }
      if (minY === -1) return resolve(null)   // imagen totalmente fondo — caller decide qué hacer

      // 2. Bottom-up: última fila con contenido
      let maxY = minY
      outer2: for (let y = h - 1; y >= minY; y--) {
        for (let x = 0; x < w; x++) {
          if (isContent(x, y)) { maxY = y; break outer2 }
        }
      }

      // 3. Left-to-right: primera columna con contenido (acotada a minY..maxY)
      let minX = 0
      outer3: for (let x = 0; x < w; x++) {
        for (let y = minY; y <= maxY; y++) {
          if (isContent(x, y)) { minX = x; break outer3 }
        }
      }

      // 4. Right-to-left: última columna con contenido
      let maxX = w - 1
      outer4: for (let x = w - 1; x >= minX; x--) {
        for (let y = minY; y <= maxY; y++) {
          if (isContent(x, y)) { maxX = x; break outer4 }
        }
      }

      // Aplicar padding y clamp a los límites del canvas
      minX = Math.max(0,     minX - padding)
      minY = Math.max(0,     minY - padding)
      maxX = Math.min(w - 1, maxX + padding)
      maxY = Math.min(h - 1, maxY + padding)

      const cropW = maxX - minX + 1
      const cropH = maxY - minY + 1

      const out = document.createElement('canvas')
      out.width  = cropW
      out.height = cropH
      out.getContext('2d').drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH)
      resolve(out.toDataURL('image/png'))
    }
    img.onerror = () => resolve(dataUrl)
    img.src = dataUrl
  })
}
